/**
 * Session storage utilities for field selection persistence
 * Manages storage and retrieval of user field preferences by export type
 */

import { ExportField, SelectedField } from '../components/FieldSelector'

export interface StoredFieldSelection {
  [fieldId: string]: {
    enabled: boolean
    order: number
  }
}

export class FieldSelectionStorage {
  private static getStorageKey(exportType: string): string {
    return `field_selections_${exportType}`
  }

  /**
   * Store field selections for a specific export type
   */
  static store(exportType: string, selections: SelectedField[]): void {
    try {
      const storageData: StoredFieldSelection = {}
      
      selections.forEach(field => {
        storageData[field.id] = {
          enabled: field.enabled,
          order: field.order
        }
      })

      const key = this.getStorageKey(exportType)
      sessionStorage.setItem(key, JSON.stringify(storageData))
    } catch (error) {
      console.warn('Failed to store field selections:', error)
    }
  }

  /**
   * Load field selections for a specific export type
   * Returns null if no stored selections exist
   */
  static load(exportType: string): StoredFieldSelection | null {
    try {
      const key = this.getStorageKey(exportType)
      const stored = sessionStorage.getItem(key)
      
      if (!stored) {
        return null
      }

      return JSON.parse(stored) as StoredFieldSelection
    } catch (error) {
      console.warn('Failed to load field selections:', error)
      return null
    }
  }

  /**
   * Apply stored selections to a field configuration
   * Falls back to default_enabled for fields not in storage
   */
  static applyStoredSelections(
    fields: ExportField[],
    stored: StoredFieldSelection | null
  ): SelectedField[] {
    return fields.map((field, index) => {
      const storedSelection = stored?.[field.id]
      
      return {
        ...field,
        enabled: storedSelection?.enabled ?? field.default_enabled,
        order: storedSelection?.order ?? index
      }
    }).sort((a, b) => a.order - b.order)
  }

  /**
   * Clean up stored selections that don't match current field configuration
   * Removes invalid field IDs and ensures order consistency
   */
  static cleanupStoredSelections(
    exportType: string,
    validFieldIds: string[]
  ): void {
    try {
      const stored = this.load(exportType)
      if (!stored) return

      const validIdSet = new Set(validFieldIds)
      const cleaned: StoredFieldSelection = {}
      let hasChanges = false

      // Remove invalid field IDs
      Object.entries(stored).forEach(([fieldId, selection]) => {
        if (validIdSet.has(fieldId)) {
          cleaned[fieldId] = selection
        } else {
          hasChanges = true
        }
      })

      // Reorder if needed to ensure consistent ordering
      const orderedEntries = Object.entries(cleaned)
        .sort(([, a], [, b]) => a.order - b.order)

      let orderIndex = 0
      orderedEntries.forEach(([fieldId, selection]) => {
        if (selection.order !== orderIndex) {
          cleaned[fieldId] = { ...selection, order: orderIndex }
          hasChanges = true
        }
        orderIndex++
      })

      if (hasChanges) {
        const key = this.getStorageKey(exportType)
        if (Object.keys(cleaned).length === 0) {
          sessionStorage.removeItem(key)
        } else {
          sessionStorage.setItem(key, JSON.stringify(cleaned))
        }
      }
    } catch (error) {
      console.warn('Failed to cleanup field selections:', error)
    }
  }

  /**
   * Clear stored selections for a specific export type
   */
  static clear(exportType: string): void {
    try {
      const key = this.getStorageKey(exportType)
      sessionStorage.removeItem(key)
    } catch (error) {
      console.warn('Failed to clear field selections:', error)
    }
  }

  /**
   * Check if there are stored selections for an export type
   */
  static hasStoredSelections(exportType: string): boolean {
    try {
      const key = this.getStorageKey(exportType)
      return sessionStorage.getItem(key) !== null
    } catch {
      return false
    }
  }
}